/**
 * RecordingControlManager
 *
 * Responsibilities:
 * - Handle macroRecorderScreenshot requests (capture + ledger)
 * - Handle macroRecorderRecordControl requests (start/stop relay)
 * - Handle macroRecorderUploadScreenshots requests (batch PUT + clear)
 * - Maintain (index → filePath) ledger per session for batch upload
 *
 * Pattern: Single-responsibility manager
 */

import * as Sentry from '@sentry/react';
import { generators } from '@playcastdotio/Messaging';
import type { IPlayjectorService } from '../interfaces/IPlayjectorService';

/**
 * Playjector's native screenshot session id must be a UUID (it keys the on-disk
 * indexed PNG session). The macro recorder uses the takeId as that session id, so
 * a takeId that isn't a UUID cannot produce a valid capture — reject it here
 * before it can occupy the host's single in-flight screenshot slot.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

/**
 * The ShadowCam used for admin streaming. Recording must target the same
 * camera so that frames actually flow to the encoder.
 */
const ADMIN_RECORDING_CAMERA = 'ShadowCam03';

/** Inlined request/reply shapes matching libs/Messaging macroRecorder generator contracts */
interface MacroRecorderScreenshotRequestMsg {
  requestId: string;
  takeId: string;
  screenStateId?: string;
  width: number;
  height: number;
}

interface MacroRecorderScreenshotReplyMsg {
  requestId: string;
  success: boolean;
  index?: number;
  filePath?: string;
  timestampMs?: number;
  error?: string;
}

interface MacroRecorderRecordControlRequestMsg {
  requestId: string;
  takeId: string;
  command: 'start' | 'stop';
}

interface MacroRecorderRecordControlReplyMsg {
  requestId: string;
  status: 'started' | 'finished' | 'error';
  filePath?: string;
  error?: string;
}

interface MacroRecorderUploadScreenshotsRequestMsg {
  requestId: string;
  takeId: string;
  items: Array<{ index: number; screenStateId: string; uploadUrl: string }>;
}

interface MacroRecorderUploadScreenshotsReplyMsg {
  requestId: string;
  results: Array<{ screenStateId: string; success: boolean; error?: string }>;
}

export interface RecordingControlManagerDeps {
  playjectorService: IPlayjectorService;
  sendIpc: (message: unknown) => void;
  getHostAuthId: () => string;
}

export class RecordingControlManager {
  private readonly playjectorService: IPlayjectorService;
  private readonly sendIpc: (message: unknown) => void;
  private readonly getHostAuthId: () => string;

  /** Per-session ledger: sessionName → Map<index, filePath> */
  private sessionLedger = new Map<string, Map<number, string>>();

  private static readonly UPLOAD_TIMEOUT_MS = 30_000;

  /**
   * Pending screenshot upload promises keyed by requestId.
   * Resolved by `notifyScreenshotUploadComplete` when the IPC reply arrives.
   */
  private pendingUploads = new Map<
    string,
    { resolve: (v: void) => void; reject: (e: Error) => void }
  >();

  constructor(deps: RecordingControlManagerDeps) {
    this.playjectorService = deps.playjectorService;
    this.sendIpc = deps.sendIpc;
    this.getHostAuthId = deps.getHostAuthId;
  }

  // ========================================
  // Screenshot capture relay
  // ========================================

  public async handleMacroRecorderScreenshot(
    req: MacroRecorderScreenshotRequestMsg,
  ): Promise<MacroRecorderScreenshotReplyMsg> {
    const { requestId, takeId, width, height } = req;

    // takeId is the single identifier on the wire; the native screenshot session
    // is keyed by it. Deriving sessionName here (rather than trusting a redundant
    // wire field) keeps it consistent with the upload path and the reply matcher.
    const sessionName = takeId;

    Sentry.logger.info('[RecordingControlManager] macro-recorder handleMacroRecorderScreenshot entry', {
      feature: 'macro-recorder', requestId, takeId, width, height,
    });

    Sentry.addBreadcrumb({
      category: 'MACRO-SCREENSHOT',
      level: 'info',
      message: 'relay screenshot -> Playjector',
      data: { requestId, takeId, width, height },
    });

    // Backstop: an invalid takeId can never yield a valid capture and would only
    // occupy the host's single in-flight slot until it times out. Fail fast
    // without touching Playjector.
    if (!isUuid(takeId)) {
      Sentry.logger.info('[RecordingControlManager] macro-recorder rejected screenshot — invalid takeId', {
        feature: 'macro-recorder', requestId, takeId,
      });
      Sentry.captureMessage('[MACRO-SCREENSHOT] rejected screenshot — invalid takeId', {
        level: 'warning',
        extra: { requestId, takeId },
      });
      return { requestId, success: false, error: 'invalid takeId' };
    }

    try {
      const result = await this.playjectorService.captureSessionScreenshot(
        sessionName,
        width,
        height,
      );

      this.recordFilePath(sessionName, result.index, result.filePath);

      Sentry.addBreadcrumb({
        category: 'MACRO-SCREENSHOT',
        level: 'info',
        message: 'screenshot captured',
        data: { requestId, sessionName, index: result.index, filePath: result.filePath },
      });

      return {
        requestId,
        success: true,
        index: result.index,
        filePath: result.filePath,
        timestampMs: result.timestampMs,
      };
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      console.error('[RecordingControlManager] Screenshot capture failed:', error);
      Sentry.captureMessage('[MACRO-SCREENSHOT] screenshot capture failed', {
        level: 'error',
        extra: { requestId, takeId, sessionName, error },
      });
      return { requestId, success: false, error };
    }
  }

  // ========================================
  // Record control relay
  // ========================================

  public async handleRecordControl(
    req: MacroRecorderRecordControlRequestMsg,
  ): Promise<MacroRecorderRecordControlReplyMsg> {
    const { requestId, takeId, command } = req;

    Sentry.logger.info('[RecordingControlManager] macro-recorder handleRecordControl entry', {
      feature: 'macro-recorder', requestId, takeId, command,
    });

    // Backstop (mirrors the screenshot path): an empty/non-UUID takeId can never
    // match an active take. Without this guard a stray stop with takeId "" reaches
    // Playjector, is rejected as "not recording this take", and can wedge the take
    // (repeated stop retries). Fail fast without touching Playjector.
    if (!isUuid(takeId)) {
      Sentry.logger.info('[RecordingControlManager] macro-recorder rejected record control — invalid takeId', {
        feature: 'macro-recorder', requestId, takeId, command,
      });
      Sentry.captureMessage('[MACRO-RECORD] rejected record control — invalid takeId', {
        level: 'warning',
        extra: { requestId, takeId, command },
      });
      return { requestId, status: 'error', error: 'invalid takeId' };
    }

    try {
      if (command === 'start') {
        await this.playjectorService.startRecording(takeId, ADMIN_RECORDING_CAMERA);
        Sentry.logger.info('[RecordingControlManager] macro-recorder startRecording sent with camera', {
          feature: 'macro-recorder', requestId, takeId, cameraName: ADMIN_RECORDING_CAMERA,
        });

        // Register the encoder-ready waiter (resolved by notifyEncoderStatus when
        // the native recording thread reports STARTED). Fire-and-forget: the
        // 'started' reply is not gated on it, but the latency signal tells us how
        // far behind the IPC ack the real encoder start trails.
        const waitStartedAt = Date.now();
        void this.waitForEncoderReady(takeId)
          .then(() => {
            Sentry.logger.info('[RecordingControlManager] macro-recorder encoder ready', {
              feature: 'macro-recorder', requestId, takeId, latencyMs: Date.now() - waitStartedAt,
            });
          })
          .catch((err) => {
            Sentry.logger.info('[RecordingControlManager] macro-recorder encoder ready wait failed', {
              feature: 'macro-recorder', requestId, takeId,
              error: err instanceof Error ? err.message : String(err),
            });
          });

        return { requestId, status: 'started' };
      } else {
        const result = await this.playjectorService.stopRecording(takeId);
        return { requestId, status: 'finished', filePath: result.filePath };
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      console.error('[RecordingControlManager] Record control failed:', error);
      return { requestId, status: 'error', error };
    }
  }

  /**
   * Expose the encoder-ready signal: resolves when Playjector's recording
   * encoder reports STARTED (frames actually flowing) for the given take.
   * Delegates to the PlayjectorService waiter registered per take.
   */
  public waitForEncoderReady(takeId: string): Promise<void> {
    return this.playjectorService.waitForEncoderReady(takeId);
  }

  // ========================================
  // Batch screenshot upload
  // ========================================

  public async handleUploadScreenshots(
    req: MacroRecorderUploadScreenshotsRequestMsg,
  ): Promise<MacroRecorderUploadScreenshotsReplyMsg> {
    const { requestId, takeId, items } = req;
    const sessionName = takeId;
    const ledger = this.sessionLedger.get(sessionName);

    Sentry.logger.info('[RecordingControlManager] macro-recorder handleUploadScreenshots entry', {
      feature: 'macro-recorder', requestId, takeId, itemCount: items.length,
    });

    const results: Array<{ screenStateId: string; success: boolean; error?: string }> = [];
    let allSuccess = true;

    for (const item of items) {
      const filePath = ledger?.get(item.index);
      if (!filePath) {
        results.push({
          screenStateId: item.screenStateId,
          success: false,
          error: `No filePath recorded for index ${item.index}`,
        });
        allSuccess = false;
        continue;
      }

      try {
        await this.uploadFile(filePath, item.uploadUrl);
        results.push({ screenStateId: item.screenStateId, success: true });
      } catch (err) {
        const error = err instanceof Error ? err.message : String(err);
        results.push({ screenStateId: item.screenStateId, success: false, error });
        allSuccess = false;
      }
    }

    if (allSuccess) {
      this.sendScreenshotClear(sessionName);
      this.sessionLedger.delete(sessionName);
      Sentry.logger.info('[RecordingControlManager] macro-recorder batch upload complete — ledger cleared', {
        feature: 'macro-recorder', requestId, takeId, itemCount: items.length,
      });
    } else {
      Sentry.logger.info('[RecordingControlManager] macro-recorder batch upload partial failure', {
        feature: 'macro-recorder', requestId, takeId,
        failedCount: results.filter((r) => !r.success).length,
      });
    }

    return { requestId, results };
  }

  // ========================================
  // Internal helpers
  // ========================================

  private recordFilePath(sessionName: string, index: number, filePath: string): void {
    let ledger = this.sessionLedger.get(sessionName);
    if (!ledger) {
      ledger = new Map();
      this.sessionLedger.set(sessionName, ledger);
    }
    ledger.set(index, filePath);
    Sentry.logger.info('[RecordingControlManager] macro-recorder ledger record', {
      feature: 'macro-recorder', takeId: sessionName, index, ledgerSize: ledger.size,
    });
  }

  private sendScreenshotClear(sessionName: string): void {
    const hostId = this.getHostAuthId();
    this.sendIpc(generators.screenshotSession.clearScreenshotSession(hostId, sessionName));
    console.log('[RecordingControlManager] Sent screenshotClear for session:', sessionName);
  }

  /**
   * Upload a single screenshot file via the IPC envelope path.
   *
   * Sends `generators.host.screenshotUpload(...)` to host-electron, which
   * PUTs the PNG to the presigned URL and replies with
   * `screenshotUploadComplete`. The reply is routed back here by
   * `ipc.ts` → `notifyScreenshotUploadComplete`.
   */
  private async uploadFile(filePath: string, uploadUrl: string): Promise<void> {
    const requestId = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingUploads.delete(requestId);
        reject(new Error('Screenshot upload timed out'));
      }, RecordingControlManager.UPLOAD_TIMEOUT_MS);

      this.pendingUploads.set(requestId, {
        resolve: () => {
          clearTimeout(timeout);
          this.pendingUploads.delete(requestId);
          resolve();
        },
        reject: (err: Error) => {
          clearTimeout(timeout);
          this.pendingUploads.delete(requestId);
          reject(err);
        },
      });

      this.sendIpc(generators.host.screenshotUpload({ filePath, uploadUrl, requestId }));
      Sentry.logger.info('[RecordingControlManager] macro-recorder screenshotUpload IPC sent', {
        feature: 'macro-recorder', transport: 'ipc', direction: 'send', requestId,
      });
    });
  }

  /**
   * Called by the IPC handler when a `screenshotUploadComplete` reply
   * arrives from host-electron. Resolves or rejects the matching
   * pending upload promise.
   */
  public notifyScreenshotUploadComplete(result: {
    requestId: string;
    success: boolean;
    sizeBytes?: number;
    error?: string;
  }): void {
    const pending = this.pendingUploads.get(result.requestId);
    if (!pending) {
      Sentry.logger.info('[RecordingControlManager] macro-recorder notifyScreenshotUploadComplete — no pending upload', {
        feature: 'macro-recorder', requestId: result.requestId,
      });
      console.warn('[RecordingControlManager] No pending upload for requestId:', result.requestId);
      return;
    }
    Sentry.logger.info('[RecordingControlManager] macro-recorder screenshotUpload resolved', {
      feature: 'macro-recorder', requestId: result.requestId,
      success: result.success, sizeBytes: result.sizeBytes,
    });
    if (result.success) {
      pending.resolve();
    } else {
      pending.reject(new Error(result.error || 'Upload failed'));
    }
  }

  /** Visible for testing: get current ledger state */
  public getLedger(sessionName: string): Map<number, string> | undefined {
    return this.sessionLedger.get(sessionName);
  }

  /** Visible for testing: clear all ledgers */
  public clearAllLedgers(): void {
    this.sessionLedger.clear();
  }
}
